import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { admin } from "../lib/api";
import { useAuth, useCurrency } from "../lib/auth";
import { date, money, ORDER_STATUS } from "../lib/format";
import type { List, OrderSummary, ProductSummary } from "../lib/types";
import { Badge, Card, PageHeader, Spinner } from "../components/ui";

export default function Dashboard() {
  const { me } = useAuth();
  const currency = useCurrency();
  const orders = useQuery({ queryKey: ["orders", "recent"], queryFn: () => admin.get<List<OrderSummary>>("/orders?limit=100") });
  const products = useQuery({ queryKey: ["products", "all-summary"], queryFn: () => admin.get<List<ProductSummary>>("/products?limit=250") });

  if (orders.isLoading || products.isLoading) return <Spinner />;
  const os = orders.data?.data ?? [];
  const ps = products.data?.data ?? [];
  const since = Date.now() - 30 * 24 * 3600 * 1000;
  const recent = os.filter((o) => new Date(o.created_at).getTime() > since && o.status !== "cancelled");
  const revenue = recent.reduce((s, o) => s + o.total, 0);
  const pending = os.filter((o) => o.status === "pending").length;
  const lowStock = ps.filter((p) => p.status === "active" && p.available <= 3);

  return (
    <>
      <PageHeader title={`Bună, ${me?.user?.name || "comerciantule"}!`} subtitle="Situația magazinului în ultimele 30 de zile." />
      <div className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Stat label="Vânzări (30 zile)" value={money(revenue, currency)} />
        <Stat label="Comenzi (30 zile)" value={String(recent.length)} />
        <Stat label="Valoare medie comandă" value={recent.length ? money(Math.round(revenue / recent.length), currency) : "—"} />
        <Stat label="De procesat" value={String(pending)} highlight={pending > 0} to="/orders?status=pending" />
      </div>
      <div className="grid gap-6 lg:grid-cols-[2fr_1fr]">
        <Card title="Comenzi recente" actions={<Link to="/orders" className="text-xs font-semibold text-brand">Toate →</Link>}>
          {os.length === 0 ? (
            <p className="py-8 text-center text-sm text-slate-400">Încă nu ai comenzi. Distribuie magazinul!</p>
          ) : (
            <ul className="-my-2 divide-y divide-slate-100">
              {os.slice(0, 8).map((o) => (
                <li key={o.id}>
                  <Link to={`/orders/${o.id}`} className="flex items-center gap-4 py-2.5 text-sm hover:text-brand">
                    <span className="w-14 font-semibold">#{o.number}</span>
                    <span className="min-w-0 flex-1 truncate text-slate-500">{o.email}</span>
                    <Badge tone={ORDER_STATUS[o.status]?.tone}>{ORDER_STATUS[o.status]?.label ?? o.status}</Badge>
                    <span className="w-24 text-right font-semibold">{money(o.total, o.currency)}</span>
                    <span className="hidden w-36 text-right text-xs text-slate-400 xl:block">{date(o.created_at)}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </Card>
        <Card title="Stoc scăzut">
          {lowStock.length === 0 ? (
            <p className="text-sm text-slate-400">Toate produsele active au stoc.</p>
          ) : (
            <ul className="space-y-2 text-sm">
              {lowStock.slice(0, 10).map((p) => (
                <li key={p.id} className="flex justify-between gap-2">
                  <Link to={`/products/${p.id}`} className="truncate hover:text-brand">{p.title}</Link>
                  <Badge tone={p.available === 0 ? "red" : "amber"}>{p.available} buc.</Badge>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </>
  );
}

function Stat({ label, value, highlight, to }: { label: string; value: string; highlight?: boolean; to?: string }) {
  const body = (
    <div className={`card p-5 ${highlight ? "border-amber-300 bg-amber-50/50" : ""}`}>
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</div>
      <div className="mt-2 text-2xl font-bold">{value}</div>
    </div>
  );
  return to ? <Link to={to}>{body}</Link> : body;
}
